import { FlowContext } from 'src/lib/flow';
import { composePage, FlowPage, FlowPageSet, newFlowPage } from 'src/lib/flow/FlowPage';
import { isAgent } from 'src/lib/flow/pageHelpers';
import { sellerGlobalPagePath } from 'src/routes/pathBuilders';

import homebuilderPage from './default/homebuilderPage';
import relationshipPage from './default/relationshipPage';
import saleTimelinePage from './default/saleTimelinePage';

/**
 * This function creates a base isEnabled function that can be extended with
 * additional checks. This is useful here since each page in this flow has the
 * same base checks and then builds up on previous answers.
 *
 * usage: const isEnabled = createIsEnabled((answers) => !!answers['likes_pancakes'])
 *  - this will create an isEnabled that checks for homebuilderIdentifier and
 *    orePartnerIdentifier and also if the user has a truthy value for
 *    `likes_pancakes`
 */
const createIsEnabled =
  (...additionalChecks: Array<(answers: FlowContext['answers'], channel: string) => boolean>) =>
  (flowContext: FlowContext) => {
    const {
      answers,
      inputContext: { homebuilderIdentifier, orePartnerIdentifier, channel },
    } = flowContext;
    return (
      !homebuilderIdentifier &&
      !orePartnerIdentifier &&
      additionalChecks.every((ck) => ck(answers, channel))
    );
  };

const withAnswer = <T>(value: T | undefined, expectedValue: T): boolean => {
  return value === undefined || value === expectedValue;
};

const submittedBySelf = (answers: FlowContext['answers'], channel: string) =>
  withAnswer(answers['seller.relation_to_owner'], 'Self') && !isAgent(answers, channel);

const notWorkingWithHomeBuilder = (answers: FlowContext['answers']) =>
  withAnswer(answers['seller.working_with_home_builder'], false);

const isNotListed = (answers: FlowContext['answers']) =>
  withAnswer(answers['seller.listed'], false);

// exported for testing
export const isEnabled = createIsEnabled(submittedBySelf, notWorkingWithHomeBuilder, isNotListed);

const relationship: FlowPage = composePage(relationshipPage, {
  slug: () => 'partner-relationship',
  isEnabled: () => true,
});

const isHomeBuilder = composePage(homebuilderPage, {
  slug: () => 'partner-home-builder',
  isEnabled: createIsEnabled(submittedBySelf),
});

const isListed = newFlowPage({
  slug: 'partner-listed',
  autoAdvance: true,
  title: 'Is this home currently listed on the market (MLS)?',
  isEnabled: createIsEnabled(submittedBySelf, notWorkingWithHomeBuilder),
  questions: [
    {
      key: 'seller.listed',
      type: 'radio_single_select',
      options: [
        {
          label: 'Yes',
          value: 'true',
        },
        {
          label: 'No',
          value: 'false',
        },
      ],
    },
  ],
});

const saleTimeline = composePage(saleTimelinePage, {
  slug: () => 'partner-sale-timeline',
  isEnabled: createIsEnabled(submittedBySelf, notWorkingWithHomeBuilder, isNotListed),
  exitPath: ({ inputContext: { sellerInputUUID } }: FlowContext) => {
    return sellerGlobalPagePath({ token: sellerInputUUID, page: 'complete-partner' });
  },
});

export default [relationship, isHomeBuilder, isListed, saleTimeline] as FlowPageSet;
